import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, Gauge, LogOut, Mail, ScanLine, Settings2, ShieldCheck } from 'lucide-react';
import './profile.css';
import { usePrincipal } from '../auth/AuthBoundary';
import { ProfileSheets, type ProfileSheet } from './ProfileSheets';
import { useMenuFocus } from './profile-menu-focus';
import { useSignOut } from './profile-actions';
import { useNoticesLink } from './notices-link';
import { InvitationsRow } from './InvitationsRow';
import { ThemeRow } from './ProfileTheme';
import { HelpRow } from './ProfileHelpRow';

/**
 * The person, top right of every Paige dashboard screen: their initials; underneath, their
 * companies, settings, two-step sign-in, the emails and notices Paige sends them, and the way out. Click opens; clicking away, a row or
 * Escape closes. Never hover: a menu that opens on hover fights the click that closes it. The
 * workbench shows the person in its own workspace menu instead (`useSignOut` is shared).
 */
export function Profile({ tenantId }: { tenantId: string }): ReactNode {
  const principal = usePrincipal();
  const [open, setOpen] = useState(false);
  const [fromKeyboard, setFromKeyboard] = useState(false);
  const [sheet, setSheet] = useState<ProfileSheet>(null);
  useNoticesLink(() => setSheet('mail'));
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const close = () => setOpen(false);
  useCloseOnAway(root, open, close);
  const toggle = (keyboard: boolean, next: boolean) => {
    setFromKeyboard(keyboard);
    setOpen(next);
  };
  return (
    <div className="profile" ref={root}>
      <Mark name={principal.name} open={open} button={button} onToggle={toggle} />
      {open && (
        <ProfileMenu
          tenantId={tenantId}
          trigger={button}
          fromKeyboard={fromKeyboard}
          onClose={close}
          onSecondFactor={() => (close(), setSheet('second-factor'))}
          onEmails={() => (close(), setSheet('mail'))}
        />
      )}
      <ProfileSheets
        tenantId={tenantId}
        back={button}
        shown={sheet}
        onClose={() => setSheet(null)}
      />
    </div>
  );
}

/** The mark itself: a menu button showing the initials. Click toggles; ArrowDown opens onto the first row. */
function Mark({
  name,
  open,
  button,
  onToggle,
}: {
  name: string;
  open: boolean;
  button: RefObject<HTMLButtonElement | null>;
  onToggle: (keyboard: boolean, next: boolean) => void;
}): ReactNode {
  return (
    <button
      ref={button}
      type="button"
      className="profile__button"
      aria-haspopup="menu"
      aria-expanded={open}
      // Starts with the initials the button shows (WCAG 2.5.3, label in name): "click DB" presses it.
      aria-label={`${initials(name)}, ${name}: companies, settings, Paige Scan, two-step sign-in, emails, help and sign out`}
      onClick={(event) => onToggle(event.detail === 0, !open)}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        onToggle(true, true);
      }}
    >
      <span className="profile__avatar" aria-hidden="true">
        {initials(name)}
      </span>
    </button>
  );
}

function ProfileMenu({
  tenantId,
  trigger,
  fromKeyboard,
  onClose,
  onSecondFactor,
  onEmails,
}: {
  tenantId: string;
  trigger: RefObject<HTMLButtonElement | null>;
  fromKeyboard: boolean;
  onClose: () => void;
  onSecondFactor: () => void;
  onEmails: () => void;
}): ReactNode {
  const principal = usePrincipal();
  const menu = useRef<HTMLDivElement>(null);
  const { onKeyDown } = useMenuFocus(menu, trigger, true, fromKeyboard);
  return (
    <div
      className="profile__menu"
      role="menu"
      aria-label={principal.name}
      ref={menu}
      onKeyDown={onKeyDown}
    >
      <div className="profile__name">{principal.name}</div>
      <CompanyRows tenantId={tenantId} onClose={onClose} />
      <InvitationsRow index={principal.tenants.length} onClose={onClose} />
      <ThemeRow />
      <ActionRows
        tenantId={tenantId}
        back={trigger}
        onClose={onClose}
        onSecondFactor={onSecondFactor}
        onEmails={onEmails}
      />
    </div>
  );
}

/** Under the companies and the theme: settings, Paige Scan, two-step sign-in, emails, help, sign out. */
function ActionRows({
  tenantId,
  back,
  onClose,
  onSecondFactor,
  onEmails,
}: {
  tenantId: string;
  /** The profile's button: Help hands the keyboard back to it. */
  back: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onSecondFactor: () => void;
  onEmails: () => void;
}): ReactNode {
  const signOut = useSignOut();
  const principal = usePrincipal();
  // The rows go on from the companies' (the menu's stagger, `--i`).
  const from = principal.tenants.length;
  const staffRows = principal.staff ? 1 : 0;
  return (
    <>
      {principal.staff ? <AdminRow index={from} onClose={onClose} /> : null}
      <SettingsRow index={from + staffRows} tenantId={tenantId} onClose={onClose} />
      <PaigeScanRow index={from + staffRows + 1} tenantId={tenantId} onClose={onClose} />
      <AccountRows
        index={from + staffRows + 2}
        onSecondFactor={onSecondFactor}
        onEmails={onEmails}
      />
      <HelpRow index={from + staffRows + 4} tenantId={tenantId} onClose={onClose} back={back} />
      <ActionRow
        index={from + staffRows + 5}
        icon={<LogOut size={15} />}
        onClick={() => void signOut()}
      >
        Sign out
      </ActionRow>
    </>
  );
}

/** The person's own: two-step sign-in, and the emails Paige sends them. */
function AccountRows({
  index,
  onSecondFactor,
  onEmails,
}: {
  index: number;
  onSecondFactor: () => void;
  onEmails: () => void;
}): ReactNode {
  return (
    <>
      <ActionRow index={index} icon={<ShieldCheck size={15} />} onClick={onSecondFactor}>
        Two-step sign-in
      </ActionRow>
      <ActionRow index={index + 1} icon={<Mail size={15} />} onClick={onEmails}>
        Emails and notices
      </ActionRow>
    </>
  );
}

function SettingsRow({
  index,
  tenantId,
  onClose,
}: {
  index: number;
  tenantId: string;
  onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const settings = () => {
    onClose();
    void navigate({ to: '/t/$tenant/settings', params: { tenant: tenantId } });
  };
  return (
    <ActionRow index={index} icon={<Settings2 size={15} />} onClick={settings}>
      Settings
    </ActionRow>
  );
}

/**
 * Paige Scan for Windows, one click from anywhere in the dashboard: its home is Company, Scanners
 * and devices, where the download and its three uses are (Dustin, 2026-10-03: hard to find).
 */
function PaigeScanRow({
  index,
  tenantId,
  onClose,
}: {
  index: number;
  tenantId: string;
  onClose: () => void;
}): ReactNode {
  const navigate = useNavigate();
  const open = () => {
    onClose();
    void navigate({
      to: '/t/$tenant/settings/company/devices',
      params: { tenant: tenantId },
      hash: 'paige-scan',
    });
  };
  return (
    <ActionRow index={index} icon={<ScanLine size={15} />} onClick={open}>
      Paige Scan for Windows
    </ActionRow>
  );
}

/** Staff get the admin area from the dashboard too, not only from the workbench (Dustin, 2026-09-21). */
function AdminRow({ index, onClose }: { index: number; onClose: () => void }): ReactNode {
  const navigate = useNavigate();
  const admin = () => {
    onClose();
    void navigate({ to: '/admin', search: {} });
  };
  return (
    <ActionRow index={index} icon={<Gauge size={15} />} onClick={admin}>
      Admin
    </ActionRow>
  );
}

/** One plain row under the companies: an icon and a verb. */
function ActionRow({
  index,
  icon,
  onClick,
  children,
}: {
  index: number;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      role="menuitem"
      className="profile__out"
      style={{ '--i': index } as CSSProperties}
      onClick={onClick}
    >
      <span className="profile__icon" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </button>
  );
}

/** The person's companies, the current one checked; choosing one opens its jobs. */
function CompanyRows({ tenantId, onClose }: { tenantId: string; onClose: () => void }): ReactNode {
  const principal = usePrincipal();
  const navigate = useNavigate();
  return (
    <>
      {principal.tenants.map((t, i) => (
        <button
          key={t.id}
          type="button"
          style={{ '--i': i } as CSSProperties}
          role="menuitemradio"
          aria-checked={t.id === tenantId}
          onClick={() => {
            onClose();
            void navigate({ to: '/t/$tenant', params: { tenant: t.id } });
          }}
        >
          <span>{t.name}</span>
          {t.id === tenantId && <Check size={15} aria-hidden="true" />}
        </button>
      ))}
    </>
  );
}

/** A pointer outside the element or Escape closes it; Escape stops there, so the screen behind does not act on it too. */
function useCloseOnAway(
  root: RefObject<HTMLElement | null>,
  open: boolean,
  close: () => void,
): void {
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) close();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key, true);
    };
  }, [root, open, close]);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
